/* Column widths a user drags in a table header, remembered per table.
 *
 * Each header cell carries a grip on its right edge (the vertical divider
 * line); dragging it sets that column's width, a double click puts the column
 * back to its automatic width. Widths are a viewing preference of this browser,
 * so they live in localStorage under `oe_colw_<table>` and never reach the
 * server.
 *
 * Dragging a divider moves that divider and nothing else. A table stretched
 * over its box hands the width left over to the columns nobody has sized, so
 * narrowing one column used to widen the others and shift them about (owner,
 * 2026-09-29: «подтягивает левые три колонки вправо»). The first drag in a
 * table therefore pins every resizable column at the width it has on screen,
 * and a table with widths is only as wide as its columns add up to
 * (`.col-sized` in global.css): blank to its right when narrower than its box,
 * scrolling when wider. Fixed-layout tables stay as they are — their name
 * column takes whatever is left, which already keeps the others still.
 *
 *   const cols = columnWidths("stock");
 *   <th style={cols.style(key)}>… <span class="col-grip" use:cols.grip={key}></span></th>
 */

const MIN_WIDTH = 40;

function read(storageKey: string): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write(storageKey: string, widths: Record<string, number>) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    /* storage disabled — the width still holds until the page is left */
  }
}

function packs(table: HTMLTableElement): boolean {
  return getComputedStyle(table).tableLayout !== "fixed";
}

function markSized(table: HTMLTableElement | null, widths: Record<string, number>) {
  if (table) table.classList.toggle("col-sized", Object.keys(widths).length > 0 && packs(table));
}

/** The key of a resizable header cell — the one its grip carries. */
function colOf(th: HTMLTableCellElement): string | undefined {
  return th.querySelector<HTMLElement>(":scope > .col-grip")?.dataset.col || undefined;
}

/* The on-screen width of every resizable column not sized yet, all measured
 * before any is applied: each applied width lays the table out again. Kept to
 * a tenth of a pixel — whole pixels add up to a visible nudge across a row. */
function onScreen(table: HTMLTableElement, widths: Record<string, number>): Record<string, number> {
  const found: Record<string, number> = {};
  table.querySelectorAll<HTMLTableCellElement>(":scope > thead th").forEach((th) => {
    const col = colOf(th);
    const w = Math.round(th.getBoundingClientRect().width * 10) / 10;
    if (col && !widths[col] && w > 0) found[col] = w;
  });
  return found;
}

/* Follows the pointer from a grip's pointerdown until it is let go. The width
 * is taken from the column's left edge rather than added up from the start, so
 * the divider stays under the pointer even as the table settles on the first
 * drag. Followed on the window, not the grip: the pointer leaves the 8-px grip
 * the moment the column starts to move. */
function drag(e: PointerEvent, th: HTMLElement, set: (w: number) => void, done: () => void) {
  e.preventDefault();
  e.stopPropagation();
  const grab = th.getBoundingClientRect().right - e.clientX;
  document.body.classList.add("col-resizing");
  const move = (ev: PointerEvent) => {
    set(Math.max(MIN_WIDTH, Math.round(ev.clientX + grab - th.getBoundingClientRect().left)));
  };
  const end = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    document.body.classList.remove("col-resizing");
    done();
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
}

export function columnWidths(table: string) {
  const storageKey = `oe_colw_${table}`;
  const widths = $state<Record<string, number>>(typeof window === "undefined" ? {} : read(storageKey));

  return {
    /** Inline style for the header cell of `col`, or "" when left automatic. */
    style(col: string): string {
      const w = widths[col];
      return w ? `width:${w}px;min-width:${w}px;max-width:${w}px` : "";
    },

    /** The grip on the right edge of `col`'s header cell, as an action. */
    grip(node: HTMLElement, col: string) {
      node.dataset.col = col;
      markSized(node.closest("table"), widths);

      const down = (e: PointerEvent) => {
        const key = node.dataset.col;
        const th = node.closest("th");
        const tbl = node.closest("table");
        if (!key || !th || !tbl) return;
        if (packs(tbl)) Object.assign(widths, onScreen(tbl, widths));
        markSized(tbl, widths);
        drag(
          e,
          th,
          (w) => (widths[key] = w),
          () => {
            write(storageKey, widths);
            markSized(tbl, widths);
          },
        );
      };
      const reset = () => {
        const key = node.dataset.col;
        if (!key) return;
        delete widths[key];
        write(storageKey, widths);
        markSized(node.closest("table"), widths);
      };
      node.addEventListener("pointerdown", down);
      node.addEventListener("dblclick", reset);
      return {
        update(next: string) {
          node.dataset.col = next;
        },
        destroy() {
          node.removeEventListener("pointerdown", down);
          node.removeEventListener("dblclick", reset);
        },
      };
    },
  };
}

/* The same behaviour as a Svelte action, for tables whose header is plain
 * markup: `<table use:resizableColumns={"orders-detail"}>`. It finds every
 * header cell, adds the divider line and the grip, and applies the widths
 * stored for that table. Columns are keyed by `data-col` when the cell has
 * one, otherwise by its caption (icons stripped), so a column that is hidden
 * and shown again keeps its width. Group captions (colspan > 1) and captionless
 * cells (action columns) are left alone. A header that re-renders — columns
 * toggled, rows of the head redrawn — is decorated again. */

function captionKey(th: HTMLTableCellElement): string {
  if (th.dataset.col) return th.dataset.col;
  const copy = th.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('.col-grip, .material-icons, .material-icons-outlined, [aria-hidden="true"]').forEach((n) => n.remove());
  return (copy.textContent || "").replace(/\s+/g, " ").trim();
}

function applyWidth(th: HTMLElement, w: number | undefined) {
  for (const prop of ["width", "min-width", "max-width"]) {
    if (w) th.style.setProperty(prop, `${w}px`);
    else th.style.removeProperty(prop);
  }
}

export function resizableColumns(table: HTMLTableElement, key: string) {
  let storageKey = `oe_colw_${key}`;
  let widths: Record<string, number> = typeof window === "undefined" ? {} : read(storageKey);

  function applyAll() {
    table.querySelectorAll<HTMLTableCellElement>(":scope > thead th").forEach((th) => {
      const col = colOf(th);
      if (col) applyWidth(th, widths[col]);
    });
    markSized(table, widths);
  }

  function start(e: PointerEvent, th: HTMLTableCellElement, col: string) {
    if (packs(table)) {
      Object.assign(widths, onScreen(table, widths));
      applyAll();
    }
    drag(
      e,
      th,
      (w) => {
        widths[col] = w;
        applyWidth(th, w);
      },
      () => {
        write(storageKey, widths);
        markSized(table, widths);
      },
    );
  }

  function decorate() {
    table.classList.add("col-divided");
    table.querySelectorAll<HTMLTableCellElement>(":scope > thead th").forEach((th) => {
      if (th.colSpan > 1 || th.querySelector(":scope > .col-grip")) return;
      const col = captionKey(th);
      if (!col) return;
      if (getComputedStyle(th).position === "static") th.style.position = "relative";
      const grip = document.createElement("span");
      grip.className = "col-grip";
      grip.dataset.col = col;
      grip.setAttribute("aria-hidden", "true");
      grip.addEventListener("pointerdown", (e) => start(e, th, col));
      grip.addEventListener("dblclick", () => {
        delete widths[col];
        applyWidth(th, undefined);
        write(storageKey, widths);
        markSized(table, widths);
      });
      th.appendChild(grip);
      applyWidth(th, widths[col]);
    });
    markSized(table, widths);
  }

  decorate();
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((m) => m.target === table || (m.target as Element).closest?.("thead"))) decorate();
  });
  observer.observe(table, { childList: true, subtree: true });

  return {
    update(next: string) {
      if (next === key) return;
      key = next;
      storageKey = `oe_colw_${key}`;
      widths = read(storageKey);
      applyAll();
    },
    destroy() {
      observer.disconnect();
    },
  };
}
